import {
  BellRing,
  ChefHat,
  Coins,
  FileSpreadsheet,
  FolderOpen,
  Gauge,
  HeartPulse,
  Landmark,
  LayoutGrid,
  Map,
  Megaphone,
  MessageSquareWarning,
  Package,
  QrCode,
  Scale,
  Send,
  ShieldAlert,
  TrendingUp,
  Users,
  Wallet,
  CalendarCheck,
  FileBadge,
  Banknote,
  type LucideIcon,
} from 'lucide-react';
import type { Capability } from '../../lib/auth/capabilities';

/**
 * The admin navigation — order is the handoff's "final agreed order", plus
 * the Money screens the BE built that the kit has no design for (FE plan
 * §4.3: Alerts, Reconciliation, Filing calendar). Registrations and Money in
 * join when their BE asks (B5, B6) land — a nav item with nothing behind it
 * is left out rather than shown.
 *
 * `title` / `breadcrumb` are the kit's TITLES map; `searchPlaceholder` is its
 * SEARCH_PLACEHOLDER map, used once a screen wires its search to an endpoint.
 * `phase` is the FE plan phase that builds the screen (shown on placeholders).
 */
export interface NavItem {
  id: string;
  path: string;
  label: string;
  icon: LucideIcon;
  /** Read permission(s) — any one opens the screen. */
  capability: Capability | readonly Capability[];
  breadcrumb: string;
  title: string;
  searchPlaceholder?: string;
  /** The screen reads `?q=` and sends it to its endpoint — shows the topbar search. */
  searchLive?: boolean;
  /**
   * Turned on for phones: open in the drawer and usable on a small screen. The
   * rest stay listed in the drawer, disabled. See `smallScreen.ts`.
   */
  mobile?: boolean;
  phase: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

export const NAV: NavSection[] = [
  {
    label: 'Dashboard',
    items: [
      {
        id: 'dashboard',
        path: '/dashboard',
        label: 'Main KPIs',
        icon: LayoutGrid,
        capability: 'core.read',
        breadcrumb: 'Dashboard',
        title: 'Main KPIs',
        phase: 'F4',
        mobile: true,
      },
      {
        id: 'locales',
        path: '/locales',
        label: 'Community Activation',
        icon: Map,
        capability: 'core.read',
        breadcrumb: 'Dashboard',
        title: 'Community Activation',
        phase: 'F4',
        mobile: true,
      },
    ],
  },
  {
    label: 'Analytics',
    items: [
      {
        id: 'analytics',
        path: '/analytics',
        label: 'Growth & Cohorts',
        icon: TrendingUp,
        capability: 'core.read',
        breadcrumb: 'Analytics',
        title: 'Growth & Cohorts',
        phase: 'F4',
        mobile: true,
      },
    ],
  },
  {
    label: 'GTM Tools',
    items: [
      {
        id: 'campaigns',
        path: '/campaigns',
        label: 'QR & Campaigns',
        icon: QrCode,
        capability: 'core.read',
        breadcrumb: 'GTM Tools',
        title: 'QR Codes & Campaigns',
        phase: 'F6',
        mobile: true,
      },
      {
        id: 'notifications',
        path: '/notifications',
        label: 'Push & SMS',
        icon: Send,
        capability: 'core.read',
        breadcrumb: 'GTM Tools',
        title: 'Push & SMS Control Panel',
        searchPlaceholder: 'Search notifications and segments…',
        phase: 'F8',
      },
      {
        id: 'content',
        path: '/content',
        label: 'Content Library',
        icon: FolderOpen,
        capability: 'core.read',
        breadcrumb: 'GTM Tools',
        title: 'Content Library',
        searchPlaceholder: 'Search content assets…',
        phase: 'F6',
        searchLive: true,
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        id: 'chefs',
        path: '/chefs',
        label: 'Chefs',
        icon: ChefHat,
        capability: 'core.read',
        breadcrumb: 'People',
        title: 'Chefs',
        // BE searchTerm matches name, email and phone — not locale or ID.
        searchPlaceholder: 'Search chefs by name, email or phone…',
        searchLive: true,
        phase: 'F3',
        mobile: true,
      },
      {
        id: 'eaters',
        path: '/eaters',
        label: 'Eaters',
        icon: Users,
        capability: 'core.read',
        breadcrumb: 'People',
        title: 'Eaters',
        searchPlaceholder: 'Search eaters by name, email or phone…',
        searchLive: true,
        phase: 'F3',
        mobile: true,
      },
      {
        id: 'ambassadors',
        path: '/ambassadors',
        label: 'Ambassadors',
        icon: Megaphone,
        capability: 'core.read',
        breadcrumb: 'People',
        title: 'Ambassador Program',
        searchPlaceholder: 'Search ambassadors or referral codes…',
        phase: 'F8',
      },
      {
        id: 'points',
        path: '/points',
        label: 'Points',
        icon: Coins,
        capability: 'core.read',
        breadcrumb: 'People',
        title: 'Points System',
        searchPlaceholder: 'Search users by name or ID…',
        phase: 'F8',
      },
    ],
  },
  {
    label: 'Trust & Support',
    items: [
      {
        id: 'feedback',
        path: '/feedback',
        label: 'Feedback Triage',
        icon: MessageSquareWarning,
        capability: 'core.read',
        breadcrumb: 'Trust & Support',
        title: 'Feedback & Ratings Triage',
        searchPlaceholder: 'Search feedback, chefs or order IDs…',
        phase: 'F5',
      },
      {
        id: 'traceability',
        path: '/traceability',
        label: 'Traceability & Recall',
        icon: ShieldAlert,
        capability: 'core.read',
        breadcrumb: 'Trust & Support',
        title: 'Food Traceability & Recall',
        searchPlaceholder: 'Search by Order ID, Dish ID or Chef ID…',
        phase: 'F5',
      },
      {
        id: 'swag',
        path: '/swag',
        label: 'Swag Fulfillment',
        icon: Package,
        capability: 'core.read',
        breadcrumb: 'Trust & Support',
        title: 'Swag Store Fulfillment',
        searchPlaceholder: 'Search swag orders by chef or item…',
        phase: 'F8',
      },
    ],
  },
  {
    label: 'Money',
    items: [
      {
        id: 'locale-health',
        path: '/money/locale-health',
        label: 'Locale health',
        icon: HeartPulse,
        capability: 'money.health.read',
        breadcrumb: 'Money',
        title: 'Locale Health',
        phase: 'F7',
        mobile: true,
      },
      {
        id: 'alerts',
        path: '/money/alerts',
        label: 'Alerts',
        icon: BellRing,
        capability: ['money.read', 'alerts.bsc.read', 'alerts.campaign.read'],
        breadcrumb: 'Money',
        title: 'Alerts',
        phase: 'F7',
      },
      {
        id: 'nexus',
        path: '/money/nexus',
        label: 'Nexus & thresholds',
        icon: Gauge,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Nexus & Threshold Monitor',
        phase: 'F7',
      },
      {
        id: 'home-rule',
        path: '/money/home-rule',
        label: 'Colorado home rule',
        icon: Landmark,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Colorado Home Rule Monitor',
        searchPlaceholder: 'Search cities…',
        phase: 'F7',
      },
      {
        id: 'payouts',
        path: '/money/payouts',
        label: 'Payouts',
        icon: Wallet,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Payouts & Aging',
        searchPlaceholder: 'Search chefs by name, locale or city…',
        phase: 'F7',
      },
      {
        id: 'reconciliation',
        path: '/money/reconciliation',
        label: 'Reconciliation',
        icon: Scale,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Settlement Reconciliation',
        phase: 'F7',
      },
      {
        id: 'filing',
        path: '/money/filing',
        label: 'Filing calendar',
        icon: CalendarCheck,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Filing Calendar',
        phase: 'F7',
      },
      {
        id: 'registrations',
        path: '/money/registrations',
        label: 'Registrations',
        icon: FileBadge,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Tax Registrations',
        phase: 'F7',
      },
      {
        id: 'money-in',
        path: '/money/money-in',
        label: 'Money in',
        icon: Banknote,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Money In',
        phase: 'F7',
      },
      {
        id: 'reports',
        path: '/money/reports',
        label: 'Reports',
        icon: FileSpreadsheet,
        capability: 'money.read',
        breadcrumb: 'Money',
        title: 'Reports & API',
        searchPlaceholder: 'Search reports…',
        phase: 'F7',
      },
    ],
  },
];

export const NAV_ITEMS: NavItem[] = NAV.flatMap((section) => section.items);

export function navItemForPath(pathname: string): NavItem | undefined {
  return NAV_ITEMS.find((item) => pathname === item.path || pathname.startsWith(`${item.path}/`));
}

/**
 * Whether a path can be used on a small screen: its nav item is turned on for
 * phones (a detail page follows its list), or it has no nav item (Account).
 */
export function mobileAllowed(pathname: string): boolean {
  const item = navItemForPath(pathname);
  return !item || !!item.mobile;
}

/** Whether the item's read permission is held (any one of them). */
export function navAllowed(item: NavItem, allowed: (capability: Capability) => boolean): boolean {
  const caps = typeof item.capability === 'string' ? [item.capability] : item.capability;
  return caps.some(allowed);
}

/** Sections filtered to what the capabilities allow; empty sections dropped. */
export function visibleNav(allowed: (capability: Capability) => boolean): NavSection[] {
  return NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => navAllowed(item, allowed)),
  })).filter((section) => section.items.length > 0);
}

/** First screen the roles can read — Main KPIs for staff, Locale health for an admin role. */
export function homePathFor(allowed: (capability: Capability) => boolean): string {
  return NAV_ITEMS.find((item) => navAllowed(item, allowed))?.path ?? '/account';
}
